import type { OrderStatusValue } from '@my/tms-api';
import { DASHBOARD_STATUS_LABELS } from '../lib/dashboardData';

interface Segment {
  key: OrderStatusValue;
  bar: string;
  dot: string;
}

const STATUS_SEGMENTS: Segment[] = [
  { key: 'Draft', bar: 'bg-slate-300', dot: 'bg-slate-300' },
  { key: 'Open', bar: 'bg-blue-400', dot: 'bg-blue-400' },
  { key: 'InProgress', bar: 'bg-orange-400', dot: 'bg-orange-400' },
  { key: 'Completed', bar: 'bg-green-400', dot: 'bg-green-400' },
  { key: 'Cancelled', bar: 'bg-red-300', dot: 'bg-red-300' },
];

export interface OrderPipelineBarProps {
  /** All-time counts keyed by status. Zero entries are omitted from the bar. */
  counts: Record<OrderStatusValue, number>;
  /** Total order count (rendered top-right). */
  total: number;
}

/**
 * Horizontal stacked-segment bar showing the pipeline status mix, with a
 * coloured-dot legend underneath. Pure presentational — feed it counts.
 */
export function OrderPipelineBar({ counts, total }: OrderPipelineBarProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          Order Pipeline
        </span>
        <span className="text-xs text-slate-400">{total} total</span>
      </div>
      <div className="flex h-2 gap-px overflow-hidden rounded-full">
        {STATUS_SEGMENTS.map(({ key, bar }) => {
          const count = counts[key];
          if (count <= 0) return null;
          return (
            <div
              key={key}
              className={`${bar} transition-all`}
              style={{ flex: count }}
              title={`${DASHBOARD_STATUS_LABELS[key]}: ${count}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {STATUS_SEGMENTS.map(({ key, dot }) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            <span className="text-[11px] text-slate-500">
              {DASHBOARD_STATUS_LABELS[key]} <strong className="text-slate-700">{counts[key]}</strong>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
